import React, { useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import seo from "../../seo/pages.json";
import { sanityImg } from "../../utils/sanityImage";

const { site } = seo;

// og-image.png and the Sanity crop below are both 1200x630 social cards.
const CARD_SIZE = { width: 1200, height: 630 };

/** Pull title/description/path for a known static route from the central config. */
export function seoProps(path) {
  const page = seo.pages[path] || seo.pages["/"];
  return { title: page.title, description: page.description, path };
}

/**
 * Per-page SEO head tags.
 *
 * Static marketing routes also get these same tags injected into the raw
 * HTML at build time by scripts/prerender.mjs, so social crawlers (which do
 * not run JavaScript) still see them. This component covers in-app navigation
 * and dynamic routes (news / publications / gallery detail) for Googlebot,
 * which does render JavaScript.
 */
/** Marks the head tags scripts/prerender.js bakes into each HTML shell. */
const PRERENDERED_TAGS = 'head [data-rh="1"]';

export default function Seo({
  title,
  ogTitle,
  description,
  path = "",
  image,
  type = "website",
  noindex = false,
}) {
  // The prerendered tags stay in place until a page renders its own Seo, so a
  // page still waiting for Sanity (or whose fetch failed) keeps its title,
  // description and canonical. On React 19, react-helmet-async renders plain
  // <title>/<meta>/<link> elements (without data-rh) that React hoists into
  // <head>, so once they are in, the prerendered set is removed to leave one.
  useLayoutEffect(() => {
    document
      .querySelectorAll(PRERENDERED_TAGS)
      .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  }, []);

  const fullTitle = title || seo.pages["/"].title;
  // og:title and twitter:title; an article passes its full, unclipped title.
  const socialTitle = ogTitle || fullTitle;
  const desc = description || site.defaultDescription;
  const canonical = `${site.url}${path}`;
  // A Sanity image is shared as its 1200x630 crop (the same card the
  // prerender writes), not the full-size original; any other image as given.
  let ogImage = `${site.url}${site.ogImage}`;
  let ogImageSize = CARD_SIZE;
  if (image && image.includes("cdn.sanity.io")) {
    ogImage = sanityImg(image, { w: 1200, h: 630 });
  } else if (image) {
    ogImage = image.startsWith("http") ? image : `${site.url}${image}`;
    ogImageSize = null;
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {/* A noindex page (the 404) names no canonical URL: /404 itself 404s,
          and a canonical on a page that asks not to be indexed is a
          conflicting signal. Same for og:url. */}
      {!noindex && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={site.name} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={desc} />
      {!noindex && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      {ogImageSize && (
        <meta property="og:image:width" content={String(ogImageSize.width)} />
      )}
      {ogImageSize && (
        <meta
          property="og:image:height"
          content={String(ogImageSize.height)}
        />
      )}
      <meta property="og:locale" content={site.locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={site.twitter} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
